import { Alert, Loader, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react';

const formatCount = (value) => Number(value).toLocaleString();

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

const RUNNING_MESSAGES = {
  preview: {
    title: 'Preview running',
    body: 'Checking which items in the Meta catalog are not owned by any Releva feed. This usually takes a few seconds. Nothing is deleted.',
  },
  remove: {
    title: 'Removing stale items',
    body: 'Deleting the items no Releva feed owns from the Meta catalog. A catalog sync runs afterwards.',
  },
};

const previewResult = (summary) => {
  if (!summary.staleCount) {
    return {
      color: 'green',
      icon: IconCheck,
      title: 'Preview: nothing to remove',
      lines: [
        summary.catalogProductCount != null
          ? `All ${formatCount(summary.catalogProductCount)} items in the Meta catalog are owned by a Releva feed.`
          : 'Every item in the Meta catalog is owned by a Releva feed.',
      ],
    };
  }
  const countsLine =
    summary.catalogProductCount != null && summary.feedOwnedCount != null
      ? `The Meta catalog has ${formatCount(summary.catalogProductCount)} items and the Releva feeds own ${formatCount(summary.feedOwnedCount)}. `
      : '';
  return {
    color: 'blue',
    icon: IconInfoCircle,
    title: `Preview: ${formatCount(summary.staleCount)} items would be deleted`,
    lines: [
      'Nothing has been deleted yet.',
      `${countsLine}The other ${formatCount(summary.staleCount)} are left over from earlier catalog syncs. No sync updates them any more, so ads can keep showing them as in stock after the product is gone.`,
      summary.sampleIds?.length > 0 ? `Examples: ${summary.sampleIds.join(', ')}` : null,
      'Click "Remove stale items" to delete them.',
    ],
  };
};

const removeResult = (summary) => {
  if (!summary.deleted) {
    return { color: 'green', icon: IconCheck, title: 'Nothing to remove', lines: [] };
  }
  return {
    color: 'green',
    icon: IconCheck,
    title: `Removed ${formatCount(summary.deleted)} stale items`,
    lines: ['A catalog sync has been started to refresh the remaining items.'],
  };
};

const problemResult = (verdict, summary) => {
  const reason = summary.reason || summary.error;
  if (verdict === 'partial') {
    return {
      color: 'yellow',
      icon: IconAlertTriangle,
      title: 'Cleanup partly applied',
      lines: [
        summary.deleted != null && summary.staleCount != null
          ? `Deleted ${formatCount(summary.deleted)} of ${formatCount(summary.staleCount)} stale items.`
          : null,
        summary.failedChunks ? `${formatCount(summary.failedChunks)} delete batches failed.` : null,
        summary.batchErrors ? `Meta reported errors for ${formatCount(summary.batchErrors)} items.` : null,
        reason,
      ],
    };
  }
  if (verdict === 'timeout') {
    return {
      color: 'orange',
      icon: IconAlertTriangle,
      title: 'Meta did not confirm the deletes in time',
      lines: ['The deletes were sent. Check the catalog counts after the next catalog sync.', reason],
    };
  }
  if (verdict === 'skipped') {
    return { color: 'gray', icon: IconInfoCircle, title: 'Cleanup skipped', lines: [reason] };
  }
  return {
    color: 'red',
    icon: IconX,
    title: summary.dryRun ? 'Preview failed' : 'Cleanup failed',
    lines: [
      reason || 'The catalog cleanup did not complete.',
      summary.dryRun
        ? 'Nothing was deleted.'
        : 'Some items may already have been deleted. Run a preview to see what is left.',
    ],
  };
};

export const describeCatalogCleanupResult = (verdict, summary) => {
  if (verdict !== 'ok') {
    return problemResult(verdict, summary);
  }
  return summary.dryRun ? previewResult(summary) : removeResult(summary);
};

const FacebookCatalogCleanupResult = ({ facebookAccount, activeRun = null, stillRunning = false }) => {
  if (!activeRun && stillRunning) {
    return (
      <Alert
        color="gray"
        title="Cleanup still running"
        icon={<IconInfoCircle size={18} />}
        data-testid="facebook-catalog-cleanup-result"
      >
        The cleanup did not report a result within 10 minutes. It may still be running. Reload the page later to see
        its result, and do not start another run until then.
      </Alert>
    );
  }

  if (activeRun) {
    const running = RUNNING_MESSAGES[activeRun];
    return (
      <Alert
        color="gray"
        title={running.title}
        icon={<Loader size="xs" />}
        data-testid="facebook-catalog-cleanup-result"
      >
        {running.body}
      </Alert>
    );
  }

  const cleanedUpAt = facebookAccount?.lastCatalogCleanupAt && formatDate(facebookAccount.lastCatalogCleanupAt);
  if (!cleanedUpAt) {
    return null;
  }

  const result = describeCatalogCleanupResult(
    facebookAccount.lastCatalogCleanupVerdict,
    facebookAccount.lastCatalogCleanupSummary || {}
  );
  const Icon = result.icon;
  return (
    <Alert
      color={result.color}
      title={result.title}
      icon={<Icon size={18} />}
      data-testid="facebook-catalog-cleanup-result"
    >
      <Stack gap={4}>
        {result.lines.filter(Boolean).map((line) => (
          <Text key={line} size="sm">
            {line}
          </Text>
        ))}
        <Text size="xs" c="dimmed">
          {facebookAccount.lastCatalogCleanupSummary?.dryRun ? 'Previewed' : 'Ran'} {cleanedUpAt}
        </Text>
      </Stack>
    </Alert>
  );
};

export default FacebookCatalogCleanupResult;
